import type { SyntheticEvent } from 'react';
import React, { useMemo } from 'react';
import classNames from 'classnames';

import { z } from 'zod';
import type { Writable } from 'type-fest';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-react-renderer';
import type { ContentComponent } from '$cms/ContentComponent';
import type { SchemaType } from '$util/schema';
import { CmsNumberSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import { VrmLookupModal } from '$quantum/molecules/VrmLookupModal';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { ServiceCard } from '@/components/molecules/ServiceCard';
import { Col, Row } from '@/components/atoms/Grid';
import { DesignType } from '@/components/atoms/Button';
import { SectionDivider } from '@/components/atoms/SectionDivider';
import { Icon } from '@/components/atoms/Icon';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import type { MakeContentfulRichTextRendererOptions } from '@/cms/richTextContentHelpers';
import { makeContentfulRichTextRendererOptions, renderRichTextContent } from '@/cms/richTextContentHelpers';
import styles from './index.module.scss';

const OurServicesSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    cardName: z.record(z.union([CmsRichTextSchema, CmsShortTextSchema])),
    cardPrice: z.record(CmsShortTextSchema),
    cardIcon: z.record(CmsShortTextSchema),
    cardColorIndex: z.record(CmsNumberSchema),
    cardCta: z.record(CmsShortTextSchema),
    cardActiveTab: z.record(CmsShortTextSchema),
  }),
};

export type OurServicesTranslatedEntities = SchemaType<(typeof OurServicesSchema)['translatedEntities']>;

const renderRichTextCardName = (content: Document): React.ReactNode => {
  const options: Writable<MakeContentfulRichTextRendererOptions> = {
    renderNode: Object.values(BLOCKS).reduce(
      (acc, current) => {
        acc[current] = (_text, children) => children;
        return acc;
      },
      {} as Required<Options>['renderNode']
    ),
  };

  const contentText = renderRichTextContent(content, makeContentfulRichTextRendererOptions(options));
  return contentText;
};

interface CardData {
  readonly name: React.ReactNode;
  readonly price: string;
  readonly icon: string;
  readonly colorIndex: number;
  readonly cta: string;
  readonly activeTab: string;
}

export const OurServices: ContentComponent<OurServicesTranslatedEntities> = function OurServices({
  translatedEntities,
  className,
  children,
  ...props
}) {
  const { heading, cardName, cardPrice, cardIcon, cardColorIndex, cardCta, cardActiveTab } =
    translatedEntities;

  const cardData: CardData[] = useMemo(() => {
    const cardNames = Object.values(cardName).map((card) => {
      if ('content' in card) return renderRichTextCardName(card.content);
      return card.value;
    });
    const cardPrices = Object.values(cardPrice).map(({ value }) => value);
    const cardIcons = Object.values(cardIcon).map(({ value }) => value);
    const cardColorIndexes = Object.values(cardColorIndex).map(({ number }) => number);
    const cardCtas = Object.values(cardCta).map(({ value }) => value);
    const cardActiveTabs = Object.values(cardActiveTab).map(({ value }) => value);
    return cardNames.map((name, index) => ({
      name,
      price: cardPrices[index],
      icon: cardIcons[index],
      colorIndex: cardColorIndexes[index],
      cta: cardCtas[index],
      activeTab: cardActiveTabs[index],
    }));
  }, [cardActiveTab, cardColorIndex, cardCta, cardIcon, cardName, cardPrice]);

  const colorsArray = [styles.color1, styles.color2, styles.color3, styles.color4];

  const makeHeadingValue = (name: React.ReactNode, icon: string): React.ReactNode => (
    <>
      <Icon
        id={icon}
        width={32}
        height={32}
        legacy={false}
        className={classNames(styles.headingIcon, styles.headingIconDesktop)}
      />
      <Icon
        id={icon}
        width={24}
        height={24}
        legacy={false}
        className={classNames(styles.headingIcon, styles.headingIconMobile)}
      />
      {name}
    </>
  );

  const handleClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    const { target } = event;
    (target as HTMLElement).closest(`.${styles.serviceCardCol}`)?.querySelector('button')?.click();
  };

  const makeCta = (ctaDesktop: string): React.ReactNode => (
    <>
      <span className={styles.ctaDesktop}>{ctaDesktop}</span>
      <span className={styles.ctaMobile}>
        <Icon id="arrows/arrow-circle-broken-right" width={24} height={24} legacy={false} />
      </span>
    </>
  );

  return (
    <ContentComponentSection
      className={classNames(OurServices.displayName, styles.component, className)}
      {...props}
    >
      <Row>
        <Col>
          <SectionDivider />
          <HeadingLevels semanticLevel={2} className={styles.heading}>
            {heading.value}
          </HeadingLevels>
        </Col>
      </Row>
      <Row>
        {cardData.map(({ name, price, icon, colorIndex, cta, activeTab }, index) => (
          <Col
            extraSmall={2}
            small={3}
            medium={6}
            large={3}
            key={activeTab}
            className={styles.serviceCardCol}
          >
            <ServiceCard
              heading={makeHeadingValue(name, icon)}
              labelColor={colorsArray[colorIndex - 1]}
              className={classNames(styles[`serviceCard${colorIndex}`], styles.ServiceCard)}
              onClick={handleClick}
              onTouchEnd={handleClick}
            >
              <div className={styles.price}>{price}</div>
              <VrmLookupModal
                id={`serviceCardCta${index}`}
                toggleButton={{
                  children: makeCta(cta),
                  size: 'XS',
                  designType: DesignType.SECONDARY_DARK,
                  className: styles.cta,
                  noIcon: true,
                }}
                formProps={{ activeTab }}
              />
            </ServiceCard>
          </Col>
        ))}
      </Row>
    </ContentComponentSection>
  );
};
OurServices.displayName = 'OurServices';
OurServices.schema = OurServicesSchema;
